import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Trash2 } from 'lucide-react';


const GalleryIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
);
// SVG Icons
const CarIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
);

const MileageIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
);

const LicenseIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const CalendarIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const EditIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
);


const VoitureCard = ({ voiture, onEdit, onDelete, onViewPhotos }) => {
    const { t, i18n } = useTranslation();

    const cleanAdditionalPhotos = (photos) => {
        if (!photos) return [];
        return photos.map(photo => {
            if (typeof photo === 'string') {
                return photo.replace(/^.*[\\\/]/, '');
            }
            return photo.path ? photo.path.replace(/^.*[\\\/]/, '') : photo;
        });
    };

    // Format mileage
    const formatMileage = (km) => {
        if (!km || km === 0) return `0 ${t('km', 'km')}`;
        const locale = i18n.language === 'ar' ? 'ar-EG' : i18n.language === 'fr' ? 'fr-FR' : 'en-US';
        return new Intl.NumberFormat(locale).format(km) + ` ${t('km', 'km')}`;
    };

    // Format price
    const formatPrice = (price) => {
        if (!price || price === 0) return `0 ${t('currency', 'DA')}`;
        const locale = i18n.language === 'ar' ? 'ar-EG' : i18n.language === 'fr' ? 'fr-FR' : 'en-US';
        return new Intl.NumberFormat(locale).format(price) + ` ${t('currency', 'DA')}`;
    };

    // Get color class based on vehicle color
    const getColorBadge = (color) => {
        if (!color) return 'bg-gray-100 text-gray-800';

        const colorMap = {
            rouge: 'bg-red-100 text-red-800',
            bleu: 'bg-blue-100 text-blue-800',
            noir: 'bg-gray-800 text-white',
            blanc: 'bg-gray-100 text-gray-800',
            gris: 'bg-gray-300 text-gray-800',
            vert: 'bg-green-100 text-green-800',
            jaune: 'bg-yellow-100 text-yellow-800',
            orange: 'bg-orange-100 text-orange-800',
            violet: 'bg-purple-100 text-purple-800',
            marron: 'bg-yellow-600 text-white',
            argent: 'bg-gray-200 text-gray-800',
            or: 'bg-yellow-300 text-yellow-800'
        };
        return colorMap[color.toLowerCase()] || 'bg-purple-100 text-purple-800';
    };

    // Get fuel type icon and color
    const getFuelBadge = (fuel) => {
        if (!fuel) return { class: 'bg-gray-100 text-gray-800', text: t('not_available_short') };

        const fuelMap = {
            essence: { class: 'bg-green-100 text-green-800', text: t('essence') },
            diesel: { class: 'bg-blue-100 text-blue-800', text: t('diesel') },
            gpl: { class: 'bg-purple-100 text-purple-800', text: t('gpl') },
            electrique: { class: 'bg-emerald-100 text-emerald-800', text: t('electric') },
            hybride: { class: 'bg-teal-100 text-teal-800', text: t('hybrid') }
        };
        return fuelMap[fuel.toLowerCase()] || { class: 'bg-gray-100 text-gray-800', text: fuel };
    };

    const fuelInfo = getFuelBadge(voiture.carburant);

    // Handle image error
    const handleImageError = (e) => {
        e.target.style.display = 'none';
        e.target.nextSibling.style.display = 'flex';
    };

    const isRtl = i18n.language === 'ar';

    return (
        <div className={`bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 group min-h-[520px] flex flex-col ${isRtl ? 'text-right' : 'text-left'}`}>
            {/* Image */}
            <div className="relative h-48 bg-gradient-to-r from-blue-50 to-blue-100 overflow-hidden">
                {voiture.photo_vehicule ? (
                    <img
                        src={`https://krili-loto.com/api/uploads/${voiture.photo_vehicule}`}
                        alt={`${voiture.marque} ${voiture.modele}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={handleImageError}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <CarIcon />
                    </div>
                )}

                {/* Price */}
                <div
                    className={`absolute top-4 ${isRtl ? 'left-4' : 'right-4'} bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg border border-white/20 flex items-center`}>
                    <span className="font-bold text-blue-600 text-sm">{formatPrice(voiture.prix_location_jour)}</span>
                    <span className={`text-xs text-gray-500 ${isRtl ? 'mr-1' : 'ml-1'}`}>{t('per_day')}</span>
                </div>

                {/* Availability */}
                <div className={`absolute top-4 ${isRtl ? 'right-4' : 'left-4'}`}>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${voiture.disponible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        <div className={`w-2 h-2 rounded-full ${isRtl ? 'ml-1' : 'mr-1'} ${voiture.disponible ? 'bg-green-500' : 'bg-red-500'
                            }`} />
                        {voiture.disponible ? t('available') : t('unavailable')}
                    </div>
                </div>
            </div>

            {/* Contenu principal + actions */}
            <div className="p-5 flex flex-col justify-between flex-1">
                <div>
                    {/* Titre */}
                    <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-bold text-gray-900 line-clamp-2 flex-1">
                            {voiture.marque || t('unknown_brand')}
                            <span
                                className={`text-blue-600 ${isRtl ? 'mr-1' : 'ml-1'}`}>{voiture.modele || t('unknown_model')} {voiture.version || ''}</span>
                        </h3>
                        <span
                            className={`text-sm font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex-shrink-0 ${isRtl ? 'mr-2' : 'ml-2'}`}>
                            {voiture.annee || t('not_available_short')}
                        </span>
                    </div>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getColorBadge(voiture.couleur)}`}>
                            {voiture.couleur || t('unknown_color')}
                        </span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${fuelInfo.class}`}>
                            {fuelInfo.text}
                        </span>
                        {voiture.boite_vitesse && (
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${voiture.boite_vitesse.toLowerCase() === 'automatique'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {voiture.boite_vitesse === 'automatique' ? t('auto') : t('manual')}
                            </span>
                        )}
                    </div>

                    <div className="border-t border-gray-100 my-4"></div>

                    {/* Infos */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                        <div className="flex items-center">
                            <LicenseIcon className={`w-4 h-4 text-gray-500 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-medium text-gray-700 truncate" title={voiture.matricule}>
                                {voiture.matricule || t('not_available_short')}
                            </span>
                        </div>
                        <div className="flex items-center">
                            <MileageIcon className={`w-4 h-4 text-gray-500 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            <span className="text-gray-600 font-medium">{formatMileage(voiture.kilometrage)}</span>
                        </div>
                        {voiture.nb_places && (
                            <div className="flex items-center">
                                <svg className={`w-4 h-4 text-gray-500 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                <span className="text-gray-600">{voiture.nb_places} {t('seats')}</span>
                            </div>
                        )}
                        {voiture.annee && (
                            <div className="flex items-center">
                                <CalendarIcon className={`w-4 h-4 text-gray-500 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                <span className="text-gray-600">{voiture.annee}</span>
                            </div>
                        )}
                    </div>

                    {/* Tarifs */}
                    {(voiture.prix_location_jour) && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                            <div className="text-xs text-gray-600 space-y-1">
                                {voiture.prix_location_jour && (
                                    <div className="flex justify-between">
                                        <span>{t('week')}</span>
                                        <span
                                            className="font-medium">{formatPrice(voiture.prix_location_jour * 7)}</span>
                                    </div>
                                )}
                                {voiture.prix_location_jour && (
                                    <div className="flex justify-between">
                                        <span>{t('month')}</span>
                                        <span className="font-medium">{formatPrice(voiture.prix_location_jour * 30)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Motif d’indisponibilité */}
                    {!voiture.disponible && voiture.motif_indisponibilite && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                            <p className="text-xs text-red-700">
                                <span className="font-medium">{t('reason')} </span>{voiture.motif_indisponibilite}
                            </p>
                        </div>
                    )}
                </div>

                {/* Boutons d'action */}
                <div className={`flex ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                    <button
                        onClick={onViewPhotos}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 hover:shadow-lg"
                    >
                        <GalleryIcon />
                        <span>{t('photos')}</span>
                    </button>
                    <button
                        onClick={() => onEdit(voiture)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 hover:shadow-lg"
                    >
                        <EditIcon />
                        <span>{t('edit')}</span>
                    </button>

                    <button onClick={() => onDelete(voiture.id)} className="text-red-600 hover:text-red-800 transition-colors px-2">
                        <Trash2 size={18} />
                    </button>

                </div>
            </div>
        </div>

    );

};

VoitureCard.propTypes = {
    voiture: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        marque: PropTypes.string,
        modele: PropTypes.string,
        version: PropTypes.string,
        annee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        couleur: PropTypes.string,
        carburant: PropTypes.string,
        boite_vitesse: PropTypes.string,
        matricule: PropTypes.string,
        kilometrage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        nb_places: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        prix_location_jour: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        prix_location_mois: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        disponible: PropTypes.bool,
        motif_indisponibilite: PropTypes.string,
        photo_vehicule: PropTypes.string,
        photos_voiture: PropTypes.arrayOf(PropTypes.string)
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onViewPhotos: PropTypes.func.isRequired // Nouvelle prop
};

export default VoitureCard;
